// ─── FormulasTab ──────────────────────────────────────────────────────────────
// Data Analytics → Formulas sub-tab (XD layout).
//   - Toolbar: zoom + undo/redo + wrap + overflow.
//   - Formula bar: function dropdown (SUM/AVERAGE/…), cancel/commit/fx, and the
//     formula input bound to the active cell.
//   - Full spreadsheet of the real selected-parameter data. Data cells are
//     read-only; the empty cells are editable and support live formulas
//     (react-spreadsheet's built-in engine evaluates `=SUM(B4:B10)` etc.).
// ──────────────────────────────────────────────────────────────────────────────

import { useState, useMemo, useRef, useEffect } from 'react'
import Spreadsheet from 'react-spreadsheet'
import { ChevronDown, MoreVertical, Undo2, Redo2, X, Check, AlignLeft, Loader } from 'lucide-react'
import { useVisualizeData } from './useVisualizeData'

const FUNCTIONS = ['SUM', 'AVERAGE', 'COUNT', 'MIN', 'MAX', 'IF', 'STDEV']
const COL_W = 150, ROW_H = 28, ROWHDR_W = 44, MIN_ROWS = 22, HEADER_ROWS = 3
const colLabel = (i) => { let s = ''; i += 1; while (i > 0) { const m = (i - 1) % 26; s = String.fromCharCode(65 + m) + s; i = Math.floor((i - 1) / 26) } return s }

export default function FormulasTab({ selectedParameters = [], dateRangeGraph, activePeriod }) {
    const [zoom, setZoom] = useState('100%')
    const [fn, setFn] = useState('SUM')
    const [active, setActive] = useState({ row: 5, column: 1 })
    const [grid, setGrid] = useState([])

    const { data, loading } = useVisualizeData(selectedParameters, dateRangeGraph, activePeriod)
    const zoomFactor = parseInt(zoom, 10) / 100
    const hasParams = selectedParameters.length > 0

    const sheetRef = useRef(null)
    const [box, setBox] = useState({ w: 0, h: 0 })
    useEffect(() => {
        const el = sheetRef.current
        if (!el || typeof ResizeObserver === 'undefined') return
        const ro = new ResizeObserver((e) => { const cr = e[0].contentRect; setBox({ w: cr.width, h: cr.height }) })
        ro.observe(el); return () => ro.disconnect()
    }, [])

    // Base matrix: data cells read-only, empty cells editable (for formulas).
    const baseMatrix = useMemo(() => {
        if (!data.length) return []
        const dataCols = data.reduce((m, r) => Math.max(m, r.length), 0)
        const fitCols = Math.max(0, Math.floor((box.w / zoomFactor - ROWHDR_W) / COL_W))
        const fitRows = Math.max(0, Math.floor((box.h / zoomFactor - ROW_H) / ROW_H))
        const totalCols = Math.max(dataCols, fitCols, 1)
        const totalRows = Math.max(data.length, fitRows, MIN_ROWS)
        const out = []
        for (let r = 0; r < totalRows; r++) {
            const src = data[r]
            const row = new Array(totalCols)
            for (let c = 0; c < totalCols; c++) {
                const cell = src && src[c]
                if (cell && (r < HEADER_ROWS || c === 0 || cell.value !== '')) row[c] = { value: cell.value, readOnly: true }
                else row[c] = { value: '' } // editable → formulas allowed
            }
            out.push(row)
        }
        return out
    }, [data, box.w, box.h, zoomFactor])

    // Reset the working grid whenever the underlying data/shape changes.
    useEffect(() => { setGrid(baseMatrix) }, [baseMatrix])

    const activeCellValue = grid[active.row]?.[active.column]?.value ?? ''
    const activeReadOnly = !!grid[active.row]?.[active.column]?.readOnly

    const setActiveCellValue = (raw) => {
        setGrid((g) => {
            const next = g.map((row) => row.slice())
            if (!next[active.row]) return g
            if (next[active.row][active.column]?.readOnly) return g
            next[active.row][active.column] = { ...next[active.row][active.column], value: raw }
            return next
        })
    }

    // Formula-bar input shows the cell value without the leading "=".
    const barText = String(activeCellValue).startsWith('=') ? String(activeCellValue).slice(1) : String(activeCellValue)
    const onBarChange = (text) => setActiveCellValue(text ? `=${text}` : '')
    const insertFunction = (name) => { setFn(name); if (!activeReadOnly) setActiveCellValue(`=${name}()`) }

    return (
        <div className="flex flex-col gap-2 flex-1 px-4 pb-4 min-h-0" style={{ fontFamily: "'Open Sans', sans-serif" }}>
            {/* Toolbar */}
            <div className="flex items-center justify-between bg-[#F1F2F4] rounded-lg px-3 py-2">
                <div className="flex items-center gap-2">
                    <select value={zoom} onChange={(e) => setZoom(e.target.value)} className="bg-gray-50 border border-gray-200 px-2 py-1 rounded text-xs text-gray-700 hover:bg-gray-100 focus:outline-none">
                        {['50%', '75%', '100%', '125%', '150%'].map((z) => <option key={z} value={z}>{z}</option>)}
                    </select>
                    <button type="button" title="Undo" disabled className="p-1 text-gray-400 cursor-not-allowed rounded"><Undo2 className="w-4 h-4" /></button>
                    <button type="button" title="Redo" disabled className="p-1 text-gray-400 cursor-not-allowed rounded"><Redo2 className="w-4 h-4" /></button>
                    <button type="button" title="Wrap text" className="p-1 text-gray-600 hover:bg-gray-100 rounded ml-1"><AlignLeft className="w-4 h-4" /></button>
                </div>
                <button type="button" className="p-1 text-gray-600 hover:bg-gray-100 rounded"><MoreVertical className="w-4 h-4" /></button>
            </div>

            {/* Formula bar */}
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded px-2 py-1.5">
                <div className="relative">
                    <select value={fn} onChange={(e) => insertFunction(e.target.value)} className="appearance-none bg-white border border-gray-200 rounded text-xs font-medium px-2 pr-6 py-1 focus:outline-none focus:border-[#358ECD] cursor-pointer">
                        {FUNCTIONS.map((name) => <option key={name} value={name}>{name}</option>)}
                    </select>
                    <ChevronDown className="w-3 h-3 text-gray-500 absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
                <button type="button" onClick={() => setActiveCellValue('')} className="p-1 text-red-500 hover:bg-red-50 rounded" title="Clear cell"><X className="w-4 h-4" /></button>
                <button type="button" className="p-1 text-green-600 hover:bg-green-50 rounded" title="Commit"><Check className="w-4 h-4" /></button>
                <button type="button" onClick={() => insertFunction(fn)} className="px-1.5 py-0.5 text-xs italic font-serif text-gray-700 hover:bg-gray-100 rounded" title="Insert function">fx</button>
                <div className="flex-1 flex items-center gap-1 border border-gray-200 rounded px-2 py-1">
                    <span className="text-xs text-gray-500 select-none">=</span>
                    <input type="text" value={barText} onChange={(e) => onBarChange(e.target.value)} disabled={activeReadOnly}
                        placeholder={activeReadOnly ? 'Data cell (read-only) — select an empty cell to enter a formula' : 'e.g. SUM(B4:B10)'}
                        className="flex-1 text-xs text-gray-800 bg-transparent focus:outline-none disabled:text-gray-400" />
                </div>
            </div>

            {/* Spreadsheet */}
            <div ref={sheetRef} className="flex-1 bg-white border border-gray-200 overflow-auto min-h-0">
                {!hasParams ? (
                    <div className="h-full flex items-center justify-center text-sm text-gray-400 p-8 text-center">Select one or more parameters to load data, then enter formulas in any empty cell.</div>
                ) : loading ? (
                    <div className="h-full flex items-center justify-center gap-2 text-sm text-gray-500"><Loader className="w-4 h-4 animate-spin" /> Loading data…</div>
                ) : (
                    <div style={{ zoom: zoomFactor }} className="dataanalytics-sheet">
                        <Spreadsheet data={grid} onChange={setGrid} onActivate={(pt) => setActive(pt)} />
                    </div>
                )}
            </div>
        </div>
    )
}
